import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { filterVideos } from "@/lib/videos";
import {
  buildFilterGroups,
  defaultFilterValues,
  PAGE_SIZE,
} from "@/lib/filterGroups";
import { categoryIntro, typeLabels } from "@/mocks/filters";
import type { VideoType } from "@/types";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel from "@/components/feature/FilterPanel";
import VideoListItem from "@/components/feature/VideoListItem";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";

const VALID_TYPES: VideoType[] = ["movie", "tv", "variety"];

export default function CategoryPage() {
  const { type = "" } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterOpen, setFilterOpen] = useState(false);

  const validType = VALID_TYPES.includes(type as VideoType);
  const info = validType ? categoryIntro[type] : null;

  usePageMeta({
    title: validType
      ? `${typeLabels[type as VideoType]}频道 - CC视频`
      : "内容分类 - CC视频",
    description: validType
      ? `${info?.desc} 在CC视频${typeLabels[type as VideoType]}频道按类型、地区、年份筛选，快速发现值得一看的作品。`
      : "CC视频内容分类页，聚合电影、电视剧、综艺资源。",
    keywords: validType
      ? `CC视频,${typeLabels[type as VideoType]},影视资源,${typeLabels[type as VideoType]}推荐`
      : "CC视频,影视分类",
  });

  const values = {
    genre: searchParams.get("genre") ?? defaultFilterValues.genre,
    region: searchParams.get("region") ?? defaultFilterValues.region,
    year: searchParams.get("year") ?? defaultFilterValues.year,
    sort: searchParams.get("sort") ?? defaultFilterValues.sort,
  };
  const page = Math.max(1, Number(searchParams.get("page") ?? "1") || 1);

  const filtered = useMemo(
    () =>
      filterVideos({
        type,
        genre: values.genre,
        region: values.region,
        year: values.year,
        sort: values.sort,
      }),
    [type, values.genre, values.region, values.year, values.sort]
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const updateParams = (patch: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([k, v]) => {
      if (!v || v === defaultFilterValues[k]) {
        next.delete(k);
      } else {
        next.set(k, v);
      }
    });
    setSearchParams(next);
  };

  const handleFilterChange = (key: string, value: string) => {
    updateParams({ [key]: value, page: undefined });
  };

  const handleReset = () => {
    setSearchParams(new URLSearchParams());
  };

  const handlePageChange = (next: number) => {
    updateParams({ page: next === 1 ? undefined : String(next) });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!validType) {
    return (
      <div className="max-w-content mx-auto px-4 md:px-6 py-16">
        <EmptyState
          icon="ri-error-warning-line"
          title="分类不存在"
          description="你访问的分类暂时不可用，请从下方入口重新选择。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const groups = buildFilterGroups();

  return (
    <div className="max-w-content mx-auto px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: "内容分类", to: "/category/movie" },
          { label: typeLabels[type as VideoType] },
        ]}
      />

      <div className="mt-4 mb-6">
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
          {info?.title}
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-500 max-w-3xl">
          {info?.desc}
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <aside className="lg:w-64 shrink-0">
          <button
            type="button"
            onClick={() => setFilterOpen((v) => !v)}
            className="lg:hidden w-full h-11 rounded-md bg-background-100 text-foreground-800 text-sm font-medium flex items-center justify-center gap-2 cursor-pointer mb-3 whitespace-nowrap"
          >
            <i className="ri-filter-3-line"></i>
            {filterOpen ? "收起筛选" : "展开筛选"}
            <i className={filterOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}></i>
          </button>

          <div
            className={`rounded-lg bg-background-50 border border-background-200 p-5 lg:sticky lg:top-20 ${
              filterOpen ? "block" : "hidden lg:block"
            }`}
          >
            <FilterPanel
              groups={groups}
              values={values}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          <div className="mb-4 flex items-center justify-between text-sm text-foreground-500">
            <span>
              共 <strong className="text-foreground-900">{filtered.length}</strong> 部作品
            </span>
            <span className="hidden sm:inline">
              第 {currentPage} / {totalPages} 页
            </span>
          </div>

          {pageItems.length === 0 ? (
            <div className="rounded-lg bg-background-50 border border-background-200">
              <EmptyState
                title="没有找到符合条件的作品"
                description="试试调整或重置筛选条件，换个类型、地区或年份再看看。"
                actionLabel="重置筛选"
                onAction={handleReset}
              />
            </div>
          ) : (
            <>
              <div className="flex flex-col gap-4">
                {pageItems.map((video) => (
                  <VideoListItem key={video.id} video={video} />
                ))}
              </div>
              <Pagination
                current={currentPage}
                total={totalPages}
                onChange={handlePageChange}
              />
            </>
          )}

          <div className="mt-8 p-5 rounded-lg bg-background-100 flex flex-col sm:flex-row sm:items-center gap-3">
            <p className="text-sm text-foreground-600 flex-1">
              没有找到想要的？试试用关键词直接搜索影视作品。
            </p>
            <Link
              to="/search"
              className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center justify-center gap-1.5 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-search-line"></i>
              去搜索
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}